import {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type DragEvent,
} from 'react';
import { faFile } from '@fortawesome/pro-regular-svg-icons';
import { cn } from '@/shared/utils/cn';
import type { AnyIconDefinition, IconFontColor, IconFontSize } from '@/shared/types/types';
import { Button } from '@/modules/Button';
import { Icon } from '@/modules/Icon';
import type { FileUploadListItemData, FileUploadListRowSize } from '../../types';
import { FileUploadListItem } from '../molecules/FileUploadListItem';
import { fileMatchesAccept } from '../../utils/matchesAccept';

const DEFAULT_DROP_ZONE = {
  title: 'Arrastra y suelta tus archivos',
  hint: 'PNG, JPG, CSV o PDF. Peso máximo 50mb.',
  selectLabel: 'Seleccionar archivos',
} as const;

const DEFAULT_DROP_ZONE_ICON = {
  name: faFile,
  size: 'xl' as IconFontSize,
  color: 'disabled' as IconFontColor,
  colorActive: 'brand' as IconFontColor,
} as const;

/** 50 MiB — mismo criterio que el texto por defecto de `dropZone.hint`. */
const DEFAULT_MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024;

const DEFAULT_CONSTRAINTS = {
  multiple: true,
  /** Alineado con el hint por defecto (PNG, JPG, CSV, PDF). */
  accept: '.png,.jpg,.jpeg,.csv,.pdf',
  maxFileSizeBytes: DEFAULT_MAX_FILE_SIZE_BYTES,
} as const;

const DEFAULT_LIST = {
  rowSize: 'lg' as FileUploadListRowSize,
};

/**
 * Capas de un icono Font Awesome duotone (mapea a `Icon` / `IconFont`).
 */
export interface FileUploaderDropZoneIconDuotoneProps {
  primary?: IconFontColor;
  secondary?: IconFontColor;
  opacityPrimary?: number;
  opacitySecondary?: number;
}

/**
 * Icono de la zona discontinua (misma superficie que `Icon`: tamaño, color y duotono).
 * Se fusiona con valores por defecto (`faFile`, `xl`, `disabled` / `brand` en highlight).
 */
export interface FileUploaderDropZoneIconProps {
  /** Definición Font Awesome del glifo. @default faFile (pro-regular). */
  name?: AnyIconDefinition;
  size?: IconFontSize;
  /** Color cuando la zona no está resaltada por arrastre. @default 'disabled' */
  color?: IconFontColor;
  /** Color cuando hay highlight de arrastre (drag over). @default 'brand' */
  colorActive?: IconFontColor;
  /** Clases en el contenedor del icono (`Icon`). */
  className?: string;
  /** Duotono: colores y opacidades por capa. */
  duotone?: FileUploaderDropZoneIconDuotoneProps;
}

/**
 * Textos y error visual de la zona de soltar (área discontinua).
 * Futuros ajustes de copy o estado de la zona viven aquí.
 */
export interface FileUploaderDropZoneProps {
  /** Título principal (Figma: «Arrastra y suelta tus archivos»). */
  title?: string;
  /** Ayuda bajo el título (tipos y peso). */
  hint?: string;
  /** Etiqueta del botón que abre el selector del sistema. */
  selectLabel?: string;
  /**
   * Mensaje bajo el botón (borde en error).
   * Sustituye el antiguo `dropZoneError` plano.
   */
  error?: string | null;
  /** Icono, tamaño, colores base/highlight y duotono (agrupado). */
  icon?: FileUploaderDropZoneIconProps;
}

/**
 * Restricciones del `<input type="file">` y validación en cliente antes de `events.onFilesAccepted`.
 */
export interface FileUploaderConstraintsProps {
  /**
   * Valor nativo `accept` (extensiones o MIME, separados por coma).
   * Por defecto: `.png,.jpg,.jpeg,.csv,.pdf` (coherente con `dropZone.hint`).
   * Pasa `undefined` explícitamente en el objeto `constraints` si no quieres filtrar por tipo.
   */
  accept?: string;
  /**
   * Tamaño máximo por archivo en bytes.
   * Por defecto: 50 MiB (mismo mensaje que el hint).
   */
  maxFileSizeBytes?: number;
  /**
   * Máximo de archivos en total en la cola (`list.items` + nuevos aceptados).
   * Si ya hay tantos ítems como este límite, no se añaden más y se llama `onValidationError`.
   * Si un lote supera el hueco restante, solo se emiten los que caben y se notifica el resto.
   */
  maxFiles?: number;
  /** Máximo de archivos por selección o soltar (un solo lote). Independiente de `maxFiles`. */
  maxFilesPerBatch?: number;
  /** Selección múltiple en el diálogo del sistema. @default true */
  multiple?: boolean;
}

/**
 * Cola de archivos y acciones por fila (`FileUploadListItem`).
 */
export interface FileUploaderListProps {
  items?: FileUploadListItemData[];
  rowSize?: FileUploadListRowSize;
  onRemoveItem?: (id: string) => void;
  /**
   * Reintento cuando `status === 'error'` (red, 5xx, validación del servidor, etc.).
   * Muestra el botón con icono `arrows-rotate` junto al de quitar (X).
   */
  onRetryItem?: (id: string) => void;
  onDeleteItem?: (id: string) => void;
}

/**
 * Eventos de ingesta: archivos que pasan o fallan la validación local del organismo.
 * La subida HTTP y los cambios de progreso siguen siendo responsabilidad del consumidor (`list.items`).
 */
export interface FileUploaderEventsProps {
  onFilesAccepted?: (files: File[]) => void;
  onValidationError?: (message: string, files: File[]) => void;
}

export interface FileUploaderProps {
  className?: string;
  disabled?: boolean;
  /** Zona discontinua: copy visible y `error` de la zona. */
  dropZone?: FileUploaderDropZoneProps;
  /** Input nativo y reglas (`accept`, peso, cupo por lote). */
  constraints?: FileUploaderConstraintsProps;
  /** Lista controlada y callbacks por fila (quitar, reintentar, borrar completado). */
  list?: FileUploaderListProps;
  /** Emisión tras validación local; no sustituye el estado de cada fila. */
  events?: FileUploaderEventsProps;
}

/**
 * Zona de arrastrar y soltar con botón «Seleccionar archivos», validación opcional de tipo y peso,
 * y lista de filas de carga (`FileUploadListItem`) según Figma *File Uploader*.
 *
 * La subida real (HTTP) queda fuera del componente: usa `events.onFilesAccepted` y actualiza `list.items`.
 *
 * @example
 * ```tsx
 * const [items, setItems] = useState<FileUploadListItemData[]>([]);
 * <FileUploader
 *   constraints={{ maxFiles: 10 }}
 *   list={{
 *     items,
 *     onRemoveItem: (id) => setItems((p) => p.filter((x) => x.id !== id)),
 *   }}
 *   events={{
 *     onFilesAccepted: (files) =>
 *       setItems((prev) => [
 *         ...prev,
 *         ...files.map((f, i) => ({
 *           id: `${Date.now()}-${i}`,
 *           fileName: f.name,
 *           format: inferFileFormat(f.name),
 *           status: 'uploading' as const,
 *           progress: 0,
 *           detailLine: `${(f.size / (1024 * 1024)).toFixed(1)} Mb | 0%`,
 *         })),
 *       ]),
 *   }}
 *   dropZone={{
 *     icon: {
 *       size: 'lg',
 *       color: 'secondary',
 *       colorActive: 'brand',
 *       duotone: { primary: 'brand', secondary: 'neutral' },
 *     },
 *   }}
 * />
 * ```
 */
export const FileUploader = forwardRef<HTMLDivElement, FileUploaderProps>(function FileUploader(
  {
    className,
    disabled = false,
    dropZone: dropZoneProp,
    constraints: constraintsProp,
    list: listProp,
    events,
  },
  ref,
) {
  const dropZone = { ...DEFAULT_DROP_ZONE, ...dropZoneProp };
  const dropZoneIcon = { ...DEFAULT_DROP_ZONE_ICON, ...dropZone.icon };
  const constraints = { ...DEFAULT_CONSTRAINTS, ...constraintsProp };
  const list = { ...DEFAULT_LIST, ...listProp };
  const { accept, maxFileSizeBytes, maxFiles, maxFilesPerBatch, multiple } = constraints;
  const { items, rowSize: itemRowSize, onRemoveItem, onRetryItem, onDeleteItem } = list;
  const { onFilesAccepted, onValidationError } = events ?? {};
  const dropZoneError = dropZone.error ?? null;

  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const isDragHighlight = isDragging && !dropZoneError && !disabled;

  const isFileDrag = useCallback((e: DragEvent<HTMLElement>) => {
    const types = e.dataTransfer?.types;
    if (!types) return false;
    return Array.from(types).some((t) => t === 'Files' || t === 'application/x-moz-file');
  }, []);

  useEffect(() => {
    if (disabled) {
      setIsDragging(false);
    }
  }, [disabled]);

  const openPicker = useCallback(() => {
    if (!disabled) inputRef.current?.click();
  }, [disabled]);

  const validateAndEmit = useCallback(
    (files: File[]) => {
      if (disabled || files.length === 0) return;

      const currentCount = items?.length ?? 0;

      if (maxFiles != null && currentCount >= maxFiles) {
        onValidationError?.(
          `Solo puedes tener hasta ${maxFiles} archivo(s); ya alcanzaste el límite.`,
          files,
        );
        return;
      }

      if (maxFilesPerBatch != null && files.length > maxFilesPerBatch) {
        onValidationError?.(
          `Solo puedes agregar hasta ${maxFilesPerBatch} archivo(s) a la vez.`,
          files,
        );
        return;
      }

      const accepted: File[] = [];

      for (const file of files) {
        if (accept && !fileMatchesAccept(file, accept)) {
          onValidationError?.(`El archivo «${file.name}» no tiene un tipo permitido.`, [file]);
          continue;
        }
        if (maxFileSizeBytes != null && file.size > maxFileSizeBytes) {
          onValidationError?.(`El archivo «${file.name}» supera el peso máximo permitido.`, [file]);
          continue;
        }
        accepted.push(file);
      }

      let toEmit = accepted;
      if (maxFiles != null && accepted.length > 0) {
        const remaining = maxFiles - currentCount;
        if (accepted.length > remaining) {
          const omitted = accepted.slice(remaining);
          toEmit = accepted.slice(0, remaining);
          onValidationError?.(
            `Solo ${remaining === 1 ? 'cabía' : 'cabían'} ${remaining} archivo(s) más (máximo ${maxFiles} en total).`,
            omitted,
          );
        }
      }

      if (toEmit.length > 0) {
        onFilesAccepted?.(toEmit);
      }
    },
    [
      disabled,
      accept,
      maxFileSizeBytes,
      maxFiles,
      maxFilesPerBatch,
      items,
      onFilesAccepted,
      onValidationError,
    ],
  );

  const onInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const listFiles = e.target.files;
      if (listFiles?.length) validateAndEmit(Array.from(listFiles));
      e.target.value = '';
    },
    [validateAndEmit],
  );

  const onDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      if (disabled) return;
      const listFiles = e.dataTransfer.files;
      if (listFiles?.length) validateAndEmit(Array.from(listFiles));
    },
    [disabled, validateAndEmit],
  );

  const onDragOver = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (disabled) return;
      if (!isFileDrag(e)) return;
      try {
        e.dataTransfer.dropEffect = 'copy';
      } catch {
        /* algunos navegadores restringen dropEffect fuera de gestos de usuario */
      }
      setIsDragging(true);
    },
    [disabled, isFileDrag],
  );

  const onDragEnter = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (disabled) return;
      if (!isFileDrag(e)) return;
      setIsDragging(true);
    },
    [disabled, isFileDrag],
  );

  /**
   * Solo quita el highlight si el puntero sale del contenedor (no al pasar a hijos).
   * El contador enter/leave provocaba parpadeos o “rebotes” al soltar.
   */
  const onDragLeave = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const related = e.relatedTarget as Node | null;
    if (related && e.currentTarget.contains(related)) return;
    setIsDragging(false);
  }, []);

  return (
    <div ref={ref} className={cn('ui-flex ui-w-full ui-flex-col ui-gap-16', className)}>
      <div
        role="group"
        aria-label={dropZone.title}
        aria-disabled={disabled || undefined}
        aria-invalid={dropZoneError ? true : undefined}
        data-file-uploader-drag-active={isDragHighlight ? 'true' : undefined}
        onClick={openPicker}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragEnter={onDragEnter}
        onDragLeave={onDragLeave}
        className={cn(
          'ui-flex ui-min-h-[250px] ui-w-full ui-cursor-pointer ui-flex-col ui-items-center ui-justify-center ui-rounded-control-sm ui-border ui-border-dashed ui-p-10 ui-outline-none',
          'ui-transition-colors ui-duration-150',
          dropZoneError
            ? 'ui-border-danger ui-bg-surface-default'
            : isDragHighlight
              ? 'ui-border-brand ui-bg-surface-brand-default'
              : 'ui-border-default ui-bg-surface-default',
          disabled && 'ui-pointer-events-none ui-opacity-50',
        )}
      >
        <input
          ref={inputRef}
          type="file"
          className="ui-sr-only"
          tabIndex={-1}
          accept={accept}
          multiple={multiple}
          disabled={disabled}
          onChange={onInputChange}
          aria-hidden
        />

        <div className="ui-flex ui-w-full ui-flex-col ui-items-center ui-gap-8">
          <div className="ui-flex ui-items-center ui-justify-center ui-leading-none">
            <Icon
              className={dropZoneIcon.className}
              name={dropZoneIcon.name}
              size={dropZoneIcon.size}
              color={isDragHighlight ? dropZoneIcon.colorActive : dropZoneIcon.color}
              colorDuotonePrimary={dropZoneIcon.duotone?.primary}
              colorDuotoneSecondary={dropZoneIcon.duotone?.secondary}
              opacityDuotonePrimary={dropZoneIcon.duotone?.opacityPrimary}
              opacityDuotoneSecondary={dropZoneIcon.duotone?.opacitySecondary}
            />
          </div>

          <div className="ui-flex ui-w-full ui-flex-col ui-items-center ui-text-center">
            <p className="ui-text-body-md-bold ui-font-body-md-bold ui-leading-body-md-bold ui-tracking-body-md ui-text-primary">
              {dropZone.title}
            </p>
            <p className="ui-mt-4 ui-text-body-xs-regular ui-font-body-xs-regular ui-leading-body-xs-regular ui-tracking-body-xs ui-text-secondary">
              {dropZone.hint}
            </p>
          </div>

          <Button
            theme="solid"
            color={isDragHighlight ? 'primary' : 'secondary'}
            size="sm"
            disabled={disabled}
            className="ui-w-[151px]"
            onClick={(e) => {
              e.stopPropagation();
              openPicker();
            }}
          >
            {dropZone.selectLabel}
          </Button>

          {dropZoneError ? (
            <p
              className="ui-mt-8 ui-max-w-full ui-text-body-xs-regular ui-font-body-xs-regular ui-text-danger"
              role="alert"
            >
              {dropZoneError}
            </p>
          ) : null}
        </div>
      </div>

      {items && items.length > 0 ? (
        <ul
          className="ui-m-0 ui-flex ui-list-none ui-flex-col ui-gap-16 ui-p-0"
          aria-label="Archivos en cola de carga"
        >
          {items.map((item) => (
            <li key={item.id} className="ui-block">
              <FileUploadListItem
                item={item}
                rowSize={itemRowSize}
                onRemove={onRemoveItem}
                onRetry={onRetryItem}
                onDelete={onDeleteItem}
              />
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
});

FileUploader.displayName = 'FileUploader';
